// Health Scoring Engine - Rules-based with ML-ready architecture

import type {
  BasicHealthData,
  DetailedHealthData,
  HealthScore,
  CategoryScore,
  ScoreFactor,
  Recommendation
} from '../types/health';

// BMI Calculation
export const calculateBMI = (height: number, weight: number): number => {
  const heightInMeters = height / 100;
  return weight / (heightInMeters * heightInMeters);
};

// Category Weights (must sum to 1.0)
const CATEGORY_WEIGHTS = {
  cardiovascular: 0.23,
  metabolic: 0.27, // Increased to give more weight to nutrition
  lifestyle: 0.25,
  recovery: 0.15,
  mental: 0.10
};

// Cardiovascular Score
const calculateCardiovascularScore = (
  basic: BasicHealthData,
  detailed?: DetailedHealthData
): CategoryScore => {
  const factors: ScoreFactor[] = [];
  let baseScore = 70;

  // Smoking impact
  if (basic.smokingStatus === 'never') {
    factors.push({
      name: 'Non-smoker',
      impact: 'positive',
      value: 15,
      description: 'Never smoking significantly reduces cardiovascular risk'
    });
    baseScore += 15;
  } else if (basic.smokingStatus === 'former') {
    factors.push({
      name: 'Former smoker',
      impact: 'positive',
      value: 8,
      description: 'Quitting smoking improves heart health over time'
    });
    baseScore += 8;
  } else {
    factors.push({
      name: 'Current smoker',
      impact: 'negative',
      value: -25,
      description: 'Smoking is a major cardiovascular risk factor'
    });
    baseScore -= 25;
  }

  // Activity level
  const activityScores = {
    'sedentary': -15,
    'light': -5,
    'moderate': 10,
    'active': 15,
    'very-active': 20
  };
  const activityImpact = activityScores[basic.activityLevel];
  factors.push({
    name: 'Activity Level',
    impact: activityImpact > 0 ? 'positive' : 'negative',
    value: activityImpact,
    description: `${basic.activityLevel.replace('-', ' ')} lifestyle`
  });
  baseScore += activityImpact;

  // Exercise details if available
  if (detailed?.exercise) {
    const cardioMinutes = detailed.exercise.cardioMinutesPerWeek;
    if (cardioMinutes >= 150) {
      factors.push({
        name: 'Cardio Exercise',
        impact: 'positive',
        value: 10,
        description: 'Meeting recommended 150+ minutes per week'
      });
      baseScore += 10;
    } else if (cardioMinutes >= 75) {
      factors.push({
        name: 'Cardio Exercise',
        impact: 'positive',
        value: 5,
        description: 'Good cardio activity, aim for 150+ minutes'
      });
      baseScore += 5;
    }
  }

  // Lab values if available
  if (detailed?.labValues?.bloodPressure) {
    const { systolic, diastolic } = detailed.labValues.bloodPressure;
    if (systolic < 120 && diastolic < 80) {
      factors.push({
        name: 'Blood Pressure',
        impact: 'positive',
        value: 10,
        description: 'Optimal blood pressure range'
      });
      baseScore += 10;
    } else if (systolic >= 140 || diastolic >= 90) {
      factors.push({
        name: 'Blood Pressure',
        impact: 'negative',
        value: -15,
        description: 'Elevated blood pressure needs attention'
      });
      baseScore -= 15;
    }
  }

  return {
    score: Math.max(0, Math.min(100, baseScore)),
    weight: CATEGORY_WEIGHTS.cardiovascular,
    factors
  };
};

// Metabolic Score
const calculateMetabolicScore = (
  basic: BasicHealthData,
  detailed?: DetailedHealthData
): CategoryScore => {
  const factors: ScoreFactor[] = [];
  let baseScore = 70;

  // BMI calculation
  const bmi = calculateBMI(basic.height, basic.weight);
  if (bmi >= 18.5 && bmi < 25) {
    factors.push({
      name: 'Healthy Weight',
      impact: 'positive',
      value: 15,
      description: 'BMI in healthy range (18.5-24.9)'
    });
    baseScore += 15;
  } else if (bmi >= 25 && bmi < 30) {
    factors.push({
      name: 'Overweight',
      impact: 'negative',
      value: -10,
      description: 'BMI indicates overweight (25-29.9)'
    });
    baseScore -= 10;
  } else if (bmi >= 30) {
    factors.push({
      name: 'Obesity',
      impact: 'negative',
      value: -20,
      description: 'BMI indicates obesity (30+)'
    });
    baseScore -= 20;
  }

  // Chronic Conditions Impact - EACH condition reduces score by 12.5 points
  if (detailed?.medicalHistory?.chronicConditions && detailed.medicalHistory.chronicConditions.length > 0) {
    const conditionsCount = detailed.medicalHistory.chronicConditions.length;
    const conditionsImpact = -12.5 * conditionsCount;
    
    factors.push({
      name: 'Chronic Conditions',
      impact: 'negative',
      value: conditionsImpact,
      description: `${conditionsCount} chronic condition${conditionsCount > 1 ? 's' : ''} affecting metabolic health`
    });
    baseScore += conditionsImpact;
  }

  // Nutrition if available
  if (detailed?.nutrition) {
    const { 
      fruitsVeggiesDaily, 
      processedFoodFrequency, 
      whiteMeatWeekly, 
      fishWeekly, 
      fiberIntake,
      wholeFoodsPreference 
    } = detailed.nutrition;
    
    // Fruits & Vegetables
    if (fruitsVeggiesDaily >= 5) {
      factors.push({
        name: 'Fruits & Vegetables',
        impact: 'positive',
        value: 12,
        description: 'Excellent fruit and vegetable intake (5+ servings)'
      });
      baseScore += 12;
    } else if (fruitsVeggiesDaily >= 3) {
      factors.push({
        name: 'Fruits & Vegetables',
        impact: 'positive',
        value: 6,
        description: 'Good fruit and vegetable intake'
      });
      baseScore += 6;
    } else if (fruitsVeggiesDaily < 2) {
      factors.push({
        name: 'Fruits & Vegetables',
        impact: 'negative',
        value: -8,
        description: 'Low fruit and vegetable intake'
      });
      baseScore -= 8;
    }

    // White Meat
    if (whiteMeatWeekly !== undefined) {
      if (whiteMeatWeekly >= 2 && whiteMeatWeekly <= 4) {
        factors.push({
          name: 'Lean Protein',
          impact: 'positive',
          value: 8,
          description: 'Good white meat consumption for lean protein'
        });
        baseScore += 8;
      } else if (whiteMeatWeekly > 5) {
        factors.push({
          name: 'Lean Protein',
          impact: 'neutral',
          value: 3,
          description: 'High white meat consumption'
        });
        baseScore += 3;
      }
    }

    // Fish & Seafood
    if (fishWeekly !== undefined) {
      if (fishWeekly >= 2) {
        factors.push({
          name: 'Omega-3 Intake',
          impact: 'positive',
          value: 10,
          description: 'Excellent fish consumption (2+ times weekly)'
        });
        baseScore += 10;
      } else if (fishWeekly === 1) {
        factors.push({
          name: 'Omega-3 Intake',
          impact: 'positive',
          value: 5,
          description: 'Good fish consumption'
        });
        baseScore += 5;
      } else if (fishWeekly === 0) {
        factors.push({
          name: 'Omega-3 Intake',
          impact: 'negative',
          value: -5,
          description: 'Consider adding fish for omega-3 benefits'
        });
        baseScore -= 5;
      }
    }

    // Fiber Intake
    if (fiberIntake) {
      const fiberScores = { low: -8, moderate: 5, high: 10 };
      const fiberImpact = fiberScores[fiberIntake];
      factors.push({
        name: 'Fiber Intake',
        impact: fiberImpact >= 0 ? 'positive' : 'negative',
        value: fiberImpact,
        description: `${fiberIntake} fiber intake from whole grains and legumes`
      });
      baseScore += fiberImpact;
    }

    // Whole Foods Preference
    if (wholeFoodsPreference) {
      const wholeFoodsScores = { rarely: -10, sometimes: 0, mostly: 8, always: 12 };
      const wholeFoodsImpact = wholeFoodsScores[wholeFoodsPreference];
      if (wholeFoodsImpact !== 0) {
        factors.push({
          name: 'Whole Foods',
          impact: wholeFoodsImpact >= 0 ? 'positive' : 'negative',
          value: wholeFoodsImpact,
          description: `${wholeFoodsPreference} choosing whole foods over processed`
        });
        baseScore += wholeFoodsImpact;
      }
    }

    // Processed Food Frequency
    if (processedFoodFrequency === 'rarely') {
      factors.push({
        name: 'Diet Quality',
        impact: 'positive',
        value: 8,
        description: 'Low processed food consumption'
      });
      baseScore += 8;
    } else if (processedFoodFrequency === 'daily') {
      factors.push({
        name: 'Diet Quality',
        impact: 'negative',
        value: -12,
        description: 'High processed food consumption'
      });
      baseScore -= 12;
    } else if (processedFoodFrequency === 'often') {
      factors.push({
        name: 'Diet Quality',
        impact: 'negative',
        value: -6,
        description: 'Frequent processed food consumption'
      });
      baseScore -= 6;
    }
  }

  // Lab values
  if (detailed?.labValues?.bloodSugar) {
    const bloodSugar = detailed.labValues.bloodSugar;
    if (bloodSugar < 100) {
      factors.push({
        name: 'Blood Sugar',
        impact: 'positive',
        value: 10,
        description: 'Normal fasting blood sugar'
      });
      baseScore += 10;
    } else if (bloodSugar >= 126) {
      factors.push({
        name: 'Blood Sugar',
        impact: 'negative',
        value: -20,
        description: 'Elevated blood sugar needs medical attention'
      });
      baseScore -= 20;
    }
  }

  return {
    score: Math.max(0, Math.min(100, baseScore)),
    weight: CATEGORY_WEIGHTS.metabolic,
    factors
  };
};

// Lifestyle Score
const calculateLifestyleScore = (
  basic: BasicHealthData,
  detailed?: DetailedHealthData
): CategoryScore => {
  const factors: ScoreFactor[] = [];
  let baseScore = 70;

  // Alcohol
  const alcoholScores = {
    'none': 10,
    'occasional': 5,
    'moderate': 0,
    'heavy': -20
  };
  const alcoholImpact = alcoholScores[basic.alcoholStatus];
  factors.push({
    name: 'Alcohol Consumption',
    impact: alcoholImpact >= 0 ? 'positive' : 'negative',
    value: alcoholImpact,
    description: `${basic.alcoholStatus} alcohol use`
  });
  baseScore += alcoholImpact;

  // Exercise routine
  if (detailed?.exercise) {
    const strengthDays = detailed.exercise.strengthTrainingDays;
    if (strengthDays >= 2) {
      factors.push({
        name: 'Strength Training',
        impact: 'positive',
        value: 10,
        description: 'Meeting recommended 2+ days per week'
      });
      baseScore += 10;
    }
  }

  // Wearable data
  if (detailed?.wearableData?.avgSteps) {
    const steps = detailed.wearableData.avgSteps;
    if (steps >= 10000) {
      factors.push({
        name: 'Daily Steps',
        impact: 'positive',
        value: 10,
        description: 'Excellent daily movement (10,000+ steps)'
      });
      baseScore += 10;
    } else if (steps >= 7000) {
      factors.push({
        name: 'Daily Steps',
        impact: 'positive',
        value: 5,
        description: 'Good daily movement (7,000+ steps)'
      });
      baseScore += 5;
    }
  }

  return {
    score: Math.max(0, Math.min(100, baseScore)),
    weight: CATEGORY_WEIGHTS.lifestyle,
    factors
  };
};

// Recovery Score
const calculateRecoveryScore = (
  basic: BasicHealthData,
  detailed?: DetailedHealthData
): CategoryScore => {
  const factors: ScoreFactor[] = [];
  let baseScore = 70;

  if (detailed?.sleep) {
    const { averageHoursPerNight, quality, consistency } = detailed.sleep;

    // Sleep duration
    if (averageHoursPerNight >= 7 && averageHoursPerNight <= 9) {
      factors.push({
        name: 'Sleep Duration',
        impact: 'positive',
        value: 15,
        description: 'Optimal sleep duration (7-9 hours)'
      });
      baseScore += 15;
    } else if (averageHoursPerNight < 6) {
      factors.push({
        name: 'Sleep Duration',
        impact: 'negative',
        value: -15,
        description: 'Insufficient sleep affects recovery'
      });
      baseScore -= 15;
    }

    // Sleep quality
    const qualityScores = { poor: -10, fair: 0, good: 10, excellent: 15 };
    const qualityImpact = qualityScores[quality];
    factors.push({
      name: 'Sleep Quality',
      impact: qualityImpact >= 0 ? 'positive' : 'negative',
      value: qualityImpact,
      description: `${quality} sleep quality`
    });
    baseScore += qualityImpact;

    // Sleep consistency
    if (consistency === 'very-consistent') {
      factors.push({
        name: 'Sleep Consistency',
        impact: 'positive',
        value: 10,
        description: 'Consistent sleep schedule supports health'
      });
      baseScore += 10;
    }
  }

  return {
    score: Math.max(0, Math.min(100, baseScore)),
    weight: CATEGORY_WEIGHTS.recovery,
    factors
  };
};

// Mental Wellbeing Score
const calculateMentalScore = (
  basic: BasicHealthData,
  detailed?: DetailedHealthData
): CategoryScore => {
  const factors: ScoreFactor[] = [];
  let baseScore = 70;

  if (detailed?.stress) {
    const { level, managementPractices, workLifeBalance } = detailed.stress;

    // Stress level (1-10 scale, inverted)
    const stressImpact = (10 - level) * 2;
    factors.push({
      name: 'Stress Level',
      impact: stressImpact >= 0 ? 'positive' : 'negative',
      value: stressImpact,
      description: `Stress level: ${level}/10`
    });
    baseScore += stressImpact;

    // Stress management
    if (managementPractices.length >= 2) {
      factors.push({
        name: 'Stress Management',
        impact: 'positive',
        value: 10,
        description: 'Active stress management practices'
      });
      baseScore += 10;
    }

    // Work-life balance
    const balanceScores = { poor: -10, fair: 0, good: 8, excellent: 15 };
    const balanceImpact = balanceScores[workLifeBalance];
    factors.push({
      name: 'Work-Life Balance',
      impact: balanceImpact >= 0 ? 'positive' : 'negative',
      value: balanceImpact,
      description: `${workLifeBalance} work-life balance`
    });
    baseScore += balanceImpact;
  }

  return {
    score: Math.max(0, Math.min(100, baseScore)),
    weight: CATEGORY_WEIGHTS.mental,
    factors
  };
};

// Main scoring function
export const calculateHealthScore = (
  basic: BasicHealthData,
  detailed?: DetailedHealthData
): HealthScore => {
  const cardiovascular = calculateCardiovascularScore(basic, detailed);
  const metabolic = calculateMetabolicScore(basic, detailed);
  const lifestyle = calculateLifestyleScore(basic, detailed);
  const recovery = calculateRecoveryScore(basic, detailed);
  const mental = calculateMentalScore(basic, detailed);

  // Calculate overall score (0-1000 scale)
  const overallScore = Math.round(
    cardiovascular.score * cardiovascular.weight * 10 +
    metabolic.score * metabolic.weight * 10 +
    lifestyle.score * lifestyle.weight * 10 +
    recovery.score * recovery.weight * 10 +
    mental.score * mental.weight * 10
  );

  // Determine confidence based on data completeness
  let confidence: 'low' | 'medium' | 'high' = 'low';
  let confidenceRange = { min: overallScore - 100, max: overallScore + 100 };

  if (detailed?.nutrition && detailed?.exercise && detailed?.sleep) {
    confidence = 'high';
    confidenceRange = { min: overallScore - 30, max: overallScore + 30 };
  } else if (detailed?.exercise || detailed?.sleep) {
    confidence = 'medium';
    confidenceRange = { min: overallScore - 60, max: overallScore + 60 };
  }

  return {
    overall: overallScore,
    confidence,
    confidenceRange,
    categories: {
      cardiovascular,
      metabolic,
      lifestyle,
      recovery,
      mental
    },
    lastUpdated: new Date()
  };
};

// Generate personalized recommendations
export const generateRecommendations = (
  score: HealthScore,
  basic: BasicHealthData,
  detailed?: DetailedHealthData
): Recommendation[] => {
  const recommendations: Recommendation[] = [];

  // Cardiovascular recommendations
  if (score.categories.cardiovascular.score < 70) {
    if (basic.smokingStatus === 'current') {
      recommendations.push({
        category: 'Cardiovascular',
        priority: 'high',
        title: 'Consider smoking cessation',
        description: 'Quitting smoking is the single most impactful change for heart health. Consider speaking with a healthcare provider about cessation programs.',
        potentialImpact: 25
      });
    }
    if (basic.activityLevel === 'sedentary' || basic.activityLevel === 'light') {
      recommendations.push({
        category: 'Cardiovascular',
        priority: 'high',
        title: 'Increase physical activity',
        description: 'Aim for 150 minutes of moderate aerobic activity per week. Start with short walks and gradually increase duration.',
        potentialImpact: 20
      });
    }
  }

  // Metabolic recommendations
  if (score.categories.metabolic.score < 70) {
    const bmi = calculateBMI(basic.height, basic.weight);
    if (bmi >= 25) {
      recommendations.push({
        category: 'Metabolic',
        priority: 'high',
        title: 'Focus on healthy weight management',
        description: 'A combination of balanced nutrition and regular physical activity can help achieve a healthier weight range.',
        potentialImpact: 20
      });
    }
    if (!detailed?.nutrition || detailed.nutrition.fruitsVeggiesDaily < 5) {
      recommendations.push({
        category: 'Metabolic',
        priority: 'medium',
        title: 'Increase fruits and vegetables',
        description: 'Aim for 5+ servings of fruits and vegetables daily for better metabolic health and disease prevention.',
        potentialImpact: 15
      });
    }
  }

  // Recovery recommendations
  if (score.categories.recovery.score < 70) {
    if (!detailed?.sleep || detailed.sleep.averageHoursPerNight < 7) {
      recommendations.push({
        category: 'Recovery',
        priority: 'high',
        title: 'Prioritize sleep',
        description: 'Aim for 7-9 hours of quality sleep per night. Establish a consistent bedtime routine and sleep schedule.',
        potentialImpact: 18
      });
    }
  }

  // Mental wellbeing recommendations
  if (score.categories.mental.score < 70) {
    if (!detailed?.stress || detailed.stress.managementPractices.length === 0) {
      recommendations.push({
        category: 'Mental Wellbeing',
        priority: 'medium',
        title: 'Develop stress management practices',
        description: 'Consider meditation, deep breathing, yoga, or other relaxation techniques to manage daily stress.',
        potentialImpact: 15
      });
    }
  }

  // Sort by priority and potential impact
  return recommendations.sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    }
    return b.potentialImpact - a.potentialImpact;
  });
};

// Calculate data completeness percentage
export const calculateDataCompleteness = (
  basic: BasicHealthData,
  detailed?: DetailedHealthData
): number => {
  let completedFields = 8; // Basic data fields
  let totalFields = 8;

  const detailedCategories = [
    'nutrition',
    'exercise',
    'sleep',
    'stress',
    'medicalHistory',
    'familyHistory',
    'wearableData',
    'labValues'
  ];

  detailedCategories.forEach(category => {
    totalFields += 1;
    if (detailed && detailed[category as keyof DetailedHealthData]) {
      completedFields += 1;
    }
  });

  return Math.round((completedFields / totalFields) * 100);
};
